// SPDX-License-Identifier: BUSL-1.1

/**
 * Attestation indicator — a small pill on the agent detail header that
 * surfaces what the runner is actually running, plus a green/yellow/red
 * dot derived from whether its `repoSha` matches the source-of-truth
 * SHA passed in via prop.
 *
 * Data lives on `Agent.attestation` (PIO-31). Pre-PIO-31 runners send
 * no attestation — the indicator renders as yellow "unknown" so the
 * operator sees that we don't know what's running, instead of silently
 * passing the agent off as healthy.
 *
 * The expected SHA is supplied by the parent route. The pill itself is
 * pure presentation (mirroring `pi-session-panel.tsx`); when the optional
 * `agentId` is provided AND the `status-surface-drilldown` feature flag
 * is on (ADR 008 Phase 3A), the pill becomes a click target that opens
 * a SurfaceDrilldownSheet with EPISTEMIC DRIFT framing and the evidence
 * pointer. When the flag is off, behavior is identical to today —
 * non-clickable, no telemetry.
 *
 * Render rules:
 *   - attestation null               → yellow dot, "unknown" label
 *   - repoSha startsWith expectedSha → green dot, "synced" label
 *   - otherwise                      → red dot, "drifted" label + remediation hint
 *
 * Click target: the SHA text is selectable so operators can copy it
 * straight into a `git log` lookup. With the drilldown flag on, the
 * surrounding pill is also clickable.
 */

import { useState } from 'react';
import type { Attestation, ProjectedSurface } from '@/types/api';
import {
  STATUS_SURFACE_DRILLDOWN_FLAG,
  useAgentSurfaces,
} from '@/hooks/use-agent-surfaces';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { SurfaceDrilldownSheet } from '@/components/status/surface-drilldown-sheet';

interface Props {
  attestation: Attestation | null | undefined;
  /**
   * SHA of the latest cli-ceo-agents/main commit that the runner
   * SHOULD be on. Caller fetches it (e.g., via gh API or a cached value
   * surfaced by the worker). Pass null to skip the comparison and just
   * render the running SHA without a verdict.
   */
  expectedSha: string | null | undefined;
  /**
   * Optional. When provided AND the `status-surface-drilldown` feature
   * flag is on, the pill becomes a click target that opens the surface
   * drilldown sheet (ADR 008 Phase 3A). Without it, behavior is unchanged.
   */
  agentId?: string;
}

function relativeAge(ms: number): string {
  const diff = Date.now() - ms;
  if (diff < 60_000) return 'just now';
  const m = Math.floor(diff / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

/**
 * EPISTEMIC DRIFT framing for the title attribute. Only used when the
 * drilldown flag is on AND the runner is drifted — the brand vocabulary
 * earns its way into the visible surface by being needed for explanation
 * (per ADR 009 §"Vocabulary discipline rules").
 */
function epistemicDriftTitle(
  attestation: Attestation,
  expectedSha: string,
): string {
  return `EPISTEMIC DRIFT: runner reports ${attestation.repoSha} but ground truth expects ${expectedSha}. Click for evidence chain.`;
}

export function AttestationIndicator({ attestation, expectedSha, agentId }: Props) {
  const drilldownFlag = useFeatureFlag(STATUS_SURFACE_DRILLDOWN_FLAG);
  const [sheetOpen, setSheetOpen] = useState(false);

  // Hook-order rule: always call useAgentSurfaces with the same arguments
  // shape; the hook itself disables the network fetch when the flag is off
  // or agentId is missing.
  const { data } = useAgentSurfaces(agentId, {
    enabled: drilldownFlag && Boolean(agentId),
  });

  const drilldownEnabled = drilldownFlag && Boolean(agentId);
  const versionDriftSurface: ProjectedSurface | null =
    data?.surfaces.find((s) => s.type === 'version_drift') ?? null;

  // ----- Unknown attestation -------------------------------------------
  if (!attestation) {
    const pill = (
      <div
        className="inline-flex items-center gap-2 rounded-md border border-yellow-300 bg-yellow-50 px-2 py-1 text-xs text-yellow-900"
        title="Runner has not yet sent attestation. May be on pre-PIO-31 code."
      >
        <span className="inline-block h-2 w-2 rounded-full bg-yellow-500" />
        <span className="font-medium">runner: unknown</span>
        <span className="text-yellow-700">no attestation reported</span>
      </div>
    );
    return drilldownEnabled ? (
      <Drilldown
        agentId={agentId!}
        surface={versionDriftSurface}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
      >
        {pill}
      </Drilldown>
    ) : (
      pill
    );
  }

  // ----- Attested ------------------------------------------------------
  const sha = attestation.repoSha?.slice(0, 7) ?? 'unknown';
  const isSynced =
    expectedSha != null &&
    attestation.repoSha != null &&
    attestation.repoSha.startsWith(expectedSha);

  const dotClass = expectedSha == null
    ? 'bg-gray-400'
    : isSynced
      ? 'bg-green-500'
      : 'bg-red-500';

  const containerClass =
    expectedSha == null
      ? 'border-gray-300 bg-gray-50 text-gray-900'
      : isSynced
        ? 'border-green-300 bg-green-50 text-green-900'
        : 'border-red-300 bg-red-50 text-red-900';

  const verdictLabel =
    expectedSha == null ? 'running' : isSynced ? 'synced' : 'drifted';

  // Title: legacy framing by default. EPISTEMIC DRIFT framing only when
  // the drilldown flag is on AND the runner is drifted — vocabulary
  // earns its way in.
  const legacyTitle =
    expectedSha && !isSynced
      ? `Runner is on ${attestation.repoSha}. Expected ${expectedSha}. Redeploy needed.`
      : `Runner is on ${attestation.repoSha}. Booted ${relativeAge(attestation.bootedAtMs)}.`;
  const title =
    drilldownEnabled && expectedSha && !isSynced
      ? epistemicDriftTitle(attestation, expectedSha)
      : legacyTitle;

  const pill = (
    <div
      className={`inline-flex items-center gap-2 rounded-md border px-2 py-1 text-xs ${containerClass}`}
      title={title}
    >
      <span className={`inline-block h-2 w-2 rounded-full ${dotClass}`} />
      <span className="font-medium">{verdictLabel}</span>
      <code className="select-all font-mono text-[11px] opacity-80">{sha}</code>
      <span className="opacity-60">•</span>
      <span className="opacity-80">cap {attestation.capabilitiesVersion}</span>
      <span className="opacity-60">•</span>
      <span className="opacity-80">booted {relativeAge(attestation.bootedAtMs)}</span>
    </div>
  );

  return drilldownEnabled ? (
    <Drilldown
      agentId={agentId!}
      surface={versionDriftSurface}
      open={sheetOpen}
      onOpenChange={setSheetOpen}
    >
      {pill}
    </Drilldown>
  ) : (
    pill
  );
}

/**
 * Wraps the pill in a click target + drilldown sheet. Pure presentation;
 * the data fetch happens one level up via useAgentSurfaces. When the
 * surface payload hasn't loaded yet (or the request errored), the click
 * still opens the sheet but the body explains that data is loading or
 * unavailable.
 */
function Drilldown({
  agentId,
  surface,
  open,
  onOpenChange,
  children,
}: {
  agentId: string;
  surface: ProjectedSurface | null;
  open: boolean;
  onOpenChange: (next: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <>
      <button
        type="button"
        className="cursor-pointer rounded-md outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
        onClick={() => onOpenChange(true)}
        aria-label="Open code identity details"
      >
        {children}
      </button>
      <SurfaceDrilldownSheet
        agentId={agentId}
        surface={surface}
        open={open}
        onOpenChange={onOpenChange}
      />
    </>
  );
}
